import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {CircularProgress, TextField, Dialog, DialogContent, DialogTitle, DialogActions} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../Services/ServiceLocator";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {Button} from "@material-ui/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPeopleCarry} from "@fortawesome/free-solid-svg-icons";
import WorkshopTemplate from "./WorkshopTemplate";
import QrScanner from "./QrScanner";
import PartDisplay from "../Common/PartDisplay";

import { AuthenticationService } from "../../Services/AuthenticationService";
import { getDerivedFamilyUnitCount } from '../../helpers/bomFamilyQuantity';
import { groupItemsForBomTable } from '../../helpers/bomLineOrder';
import { getPartLinkPartId, requiresBomCheckScan } from '../../utils/bomContributions';

// Utility function for formatting decimal quantities
const formatQuantity = (value) => {
    if (value === null || value === undefined) return 1;
    // If it's a whole number, display as integer, otherwise show decimals
    return value % 1 === 0 ? Math.floor(value) : value;
};

// Utility function to get quantity styling
const getQuantityStyle = (value) => {
    const quantity = formatQuantity(value);
    if (quantity > 1) {
        return {
            fontWeight: 'bold',
            fontSize: '1.1em'
        };
    }
    return {};
};

const buildAssemblyOption = ({ title, value, description }) => ({
    title,
    value,
    description: description || ''
});

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    grayRow: {
        backgroundColor: '#eeeeee',
    },
    whiteRow: {
        backgroundColor: '#ffffff',
    },
    greenRow: {
        backgroundColor: '#90EE90 !important',
    },
});

class BomCheck extends React.Component {
    state = {
        loading: true,
        user: {},
        users: [],
        customDiscount: 50,
        showScanner: false,
        scannedItems: new Set(),
        bomCheckCompleted: false,
        selectedAssembly: null,
        processedAssemblyId: null, // Track which assemblyId we've already processed
        imageModalOpen: false,
        selectedImageUrl: null,
        expandedSubAssemblies: new Set(),
        expandedFamilies: new Set()
    };

    constructor(props) {
        super(props);
        globalProps = props;

        // Keep the original authorization setup
        const jwtToken = AuthenticationService.getInstance().getToken();
        if (jwtToken) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
        }
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps) {
        // If the assemblyId prop has changed, update the selected assembly
        if (this.props.assemblyId !== prevProps.assemblyId && this.props.assemblyId !== this.state.processedAssemblyId) {
            // Only process when we have assemblies and subassemblies loaded
            if (!this.state.loading && this.state.assemblies && this.state.subAssemblies) {
                this.processAssemblyIdFromProps();
            }
        }
    }

    processAssemblyIdFromProps = () => {
        const { assemblyId } = this.props;
        if (!assemblyId) return;

        // Find the assembly in our loaded data
        const assemblyMatch = this.state.assemblies.find(x => x.assembly.id === assemblyId);
        if (assemblyMatch) {
            this.setState({ 
                selectedAssembly: assemblyId,
                processedAssemblyId: assemblyId
            });
            return;
        }

        // Or check if it's a subassembly
        const subAssemblyMatch = this.state.subAssemblies.find(x => x.subAssembly.id === assemblyId);
        if (subAssemblyMatch) {
            this.setState({ 
                selectedAssembly: assemblyId,
                processedAssemblyId: assemblyId
            });
        }
    }

    loadData = () => {
        const self = this;

        Promise.all([self.loadAssemblies(), self.loadSubAssemblies(), self.loadParts()]).then(() => {
            self.setState({loading: false}, () => {
                // Process assemblyId from props after data is loaded
                if (this.props.assemblyId) {
                    this.processAssemblyIdFromProps();
                } else {
                    // Use assembly ID from route params if no props.assemblyId
                    const assemblyId = globalProps.match?.params?.assemblyId;
                    if (assemblyId) {
                        this.setState({ 
                            selectedAssembly: assemblyId,
                            processedAssemblyId: assemblyId
                        });
                    }
                }
            });
        });
    };

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllAssembliesExpanded().then((assemblies) => {
            assemblies = assemblies.sort(function (a, b) {
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                if (b.assembly.assemblyId > a.assembly.assemblyId) return -1;
                return 0;
            });
            self.setState({assemblies});
        });
    };

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllSubAssembliesExpanded().then((subAssemblies) => {
            subAssemblies = subAssemblies.sort(function (a, b) {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                return 0;
            });
            self.setState({subAssemblies});
        });
    };

    loadParts = () => {
        const self = this;
        self.setState({loading: true});
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPartsExpanded().then((parts) => {
            parts = parts.sort(function (a, b) {
                if (a.part.partId < b.part.partId) return -1;
                if (a.part.partId > b.part.partId) return 1;
                return 0;
            });
            self.setState({parts});
        });
    };

    handleAssemblyChange = (event, newValue) => {
        if (!newValue) {
            this.setState({
                selectedAssembly: null,
                processedAssemblyId: null,
                scannedItems: new Set(), // Clear scanned items when changing assembly
                bomCheckCompleted: false
            });
        } else {
            this.setState({
                selectedAssembly: newValue.value,
                processedAssemblyId: newValue.value,
                scannedItems: new Set(), // Clear scanned items when changing assembly
                bomCheckCompleted: false
            });
        }
    };

    addQrScan = (qrData) => {
        const scanData = qrData;
        
        // Find matching parts and assemblies
        let found = false;
        let scannedCount = 0;
        
        // Check if selected assembly
        if (this.state.selectedAssembly) {
            let assemblyExpandedMaybe = this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly);
            let assemblyMaybe = assemblyExpandedMaybe ? this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly).assembly : null;
            let subAssemblyExpandedMaybe = this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly);
            let subAssemblyMaybe = !assemblyMaybe && subAssemblyExpandedMaybe ? this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly).subAssembly : null;
            
            const scannedItems = new Set(this.state.scannedItems);
            
            // If we found an assembly, check its parts and subassemblies
            if (assemblyMaybe) {
                // Check if QR matches a part in this assembly
                const matchingPart = assemblyMaybe.partsToAssemblies.find(x => x.partFk === scanData);
                
                // Check if QR matches a subassembly in this assembly
                const matchingSubAssembly = assemblyMaybe.subAssembliesToAssemblies.find(x => x.subAssemblyFk === scanData);
                
                // Check if QR matches a part family ID - if so, mark all parts from that family as scanned
                const familyParts = assemblyMaybe.partsToAssemblies.filter(x => x.partFamilyId === scanData);
                
                if (matchingPart) {
                    scannedItems.add(scanData);
                    scannedCount = 1;
                    found = true;
                } else if (matchingSubAssembly) {
                    scannedItems.add(scanData);
                    scannedCount = 1;
                    found = true;
                } else if (familyParts.length > 0) {
                    // Mark all parts from the family as scanned
                    familyParts.forEach(familyPart => {
                        scannedItems.add(familyPart.partFk);
                        scannedCount++;
                    });
                    found = true;
                }
            } 
            // If we found a subassembly, check its parts
            else if (subAssemblyMaybe) {
                // Check if QR matches a part in this subassembly
                const matchingPart = subAssemblyMaybe.partsToSubAssemblies.find(x => x.partFk === scanData);
                
                // Check if QR matches a child subassembly in this subassembly
                const matchingChildSubAssembly = subAssemblyMaybe.childSubAssemblies && subAssemblyMaybe.childSubAssemblies.find(x => x.childSubAssemblyFk === scanData);
                
                // Check if QR matches a part family ID - if so, mark all parts from that family as scanned
                const familyParts = subAssemblyMaybe.partsToSubAssemblies.filter(x => x.partFamilyId === scanData);
                
                if (matchingPart) {
                    scannedItems.add(scanData);
                    scannedCount = 1;
                    found = true;
                } else if (matchingChildSubAssembly) {
                    scannedItems.add(scanData);
                    scannedCount = 1;
                    found = true;
                } else if (familyParts.length > 0) {
                    // Mark all parts from the family as scanned
                    familyParts.forEach(familyPart => {
                        scannedItems.add(familyPart.partFk);
                        scannedCount++;
                    });
                    found = true;
                }
            }
            
            if (found) {
                this.setState({ scannedItems });
                if (scannedCount === 1) {
                    window.$alertSuccess("BOM item scanned!");
                } else {
                    window.$alertSuccess(`BOM family and its ${scannedCount} parts scanned!`);
                }
            }
        }
        
        if (!found) {
            window.$alertError("No matching item found in the BOM.");
        }
    };

    getRowStyle = (connection, itemId) => {
        const { classes } = this.props;
        if (this.state.scannedItems.has(itemId)) {
            return classes.greenRow;
        }
        return classes[requiresBomCheckScan(connection) ? 'whiteRow' : 'grayRow'];
    };

    getFamilyRowStyle = (family) => {
        const { classes } = this.props;
        
        // Check if all required parts in the family are scanned
        const allRequiredPartsScanned = family.parts.every(partConnection => {
            if (!requiresBomCheckScan(partConnection)) return true;
            const expandedPart = this.state.parts.find(x => x.part.id === getPartLinkPartId(partConnection));
            if (!expandedPart || !expandedPart.part.isActive) return true;
            return this.state.scannedItems.has(expandedPart.part.id);
        });

        // Check if family has any required parts
        const hasRequiredParts = family.parts.some(partConnection => {
            if (!requiresBomCheckScan(partConnection)) return false;
            const expandedPart = this.state.parts.find(x => x.part.id === getPartLinkPartId(partConnection));
            return expandedPart && expandedPart.part.isActive;
        });

        // If family has required parts and all are scanned, show green
        if (hasRequiredParts && allRequiredPartsScanned) {
            return classes.greenRow;
        }

        return ''; // Default styling
    };

    areAllRequiredItemsScanned = () => {
        if (!this.state.selectedAssembly) return false;
        
        // If there are no items scanned yet, return false
        if (this.state.scannedItems.size === 0) return false;
        
        let allScanned = true;
        let assemblyExpandedMaybe = this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly);
        let assemblyMaybe = assemblyExpandedMaybe ? this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly).assembly : null;
        let subAssemblyExpandedMaybe = this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly);
        let subAssemblyMaybe = !assemblyMaybe && subAssemblyExpandedMaybe ? this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly).subAssembly : null;
        
        let requiredItemsExist = false;
        
        if (assemblyMaybe) {
            // Check parts
            for (const assemblyPart of assemblyMaybe.partsToAssemblies) {
                if (!requiresBomCheckScan(assemblyPart)) continue;
                const expandedPart = this.state.parts.find(x => x.part.id === getPartLinkPartId(assemblyPart));
                if (!expandedPart || !expandedPart.part.isActive) continue;

                requiredItemsExist = true;
                if (!this.state.scannedItems.has(expandedPart.part.id)) {
                    allScanned = false;
                    break;
                }
            }
            
            // Check subassemblies if all parts are scanned
            if (allScanned) {
                for (const subToAssembly of assemblyMaybe.subAssembliesToAssemblies) {
                    const subAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === subToAssembly.subAssemblyFk);
                    if (!subAssemblyExpanded || !subAssemblyExpanded.subAssembly.isActive) continue;
                    
                    // Track if we have at least one required item
                    if (subToAssembly.requiredForBomCheck) {
                        requiredItemsExist = true;
                    }
                    
                    // If required for BOM check and not scanned, set allScanned to false
                    if (subToAssembly.requiredForBomCheck && !this.state.scannedItems.has(subAssemblyExpanded.subAssembly.id)) {
                        allScanned = false;
                        break;
                    }
                }
            }
        } else if (subAssemblyMaybe) {
            // Check parts in subassembly
            for (const assemblyPart of subAssemblyMaybe.partsToSubAssemblies) {
                if (!requiresBomCheckScan(assemblyPart)) continue;
                const expandedPart = this.state.parts.find(x => x.part.id === getPartLinkPartId(assemblyPart));
                if (!expandedPart || !expandedPart.part.isActive) continue;

                requiredItemsExist = true;
                if (!this.state.scannedItems.has(expandedPart.part.id)) {
                    allScanned = false;
                    break;
                }
            }
            
            // Check child subassemblies if all parts are scanned
            if (allScanned && subAssemblyMaybe.childSubAssemblies) {
                for (const childSubAssemblyRelation of subAssemblyMaybe.childSubAssemblies) {
                    const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childSubAssemblyRelation.childSubAssemblyFk);
                    if (!childSubAssemblyExpanded || !childSubAssemblyExpanded.subAssembly.isActive) continue;
                    
                    // Track if we have at least one required item
                    if (childSubAssemblyRelation.requiredForBomCheck) {
                        requiredItemsExist = true;
                    }
                    
                    // If required for BOM check and not scanned, set allScanned to false
                    if (childSubAssemblyRelation.requiredForBomCheck && !this.state.scannedItems.has(childSubAssemblyExpanded.subAssembly.id)) {
                        allScanned = false;
                        break;
                    }
                }
            }
        }
        
        // If there are no required items, we should still allow completion
        // if at least one item has been scanned
        if (!requiredItemsExist) {
            return this.state.scannedItems.size > 0;
        }
        
        return allScanned;
    };
    
    handleCompleteBomCheck = () => {
        this.setState({ bomCheckCompleted: true });
        window.$alertSuccess("BOM Check completed successfully!");
        
        // Call the onComplete callback if provided (for modal usage)
        if (this.props.onComplete && typeof this.props.onComplete === 'function') {
            // Get the assembly or subassembly ID from selectedAssembly state
            let assemblyId = this.state.selectedAssembly;
            this.props.onComplete(assemblyId);
        }
    };

    openImageModal = (imageUrl) => {
        this.setState({
            imageModalOpen: true,
            selectedImageUrl: imageUrl
        });
    };

    closeImageModal = () => {
        this.setState({
            imageModalOpen: false,
            selectedImageUrl: null
        });
    };

    getFirstImageUrl = (imageURLs) => {
        if (!imageURLs || imageURLs.trim() === '') return null;
        const urls = imageURLs.split('~|~');
        return urls[0] || null;
    };

    renderBomMemo = (bomNotes) => {
        const text = (bomNotes || '').trim();
        if (!text) return null;

        return (
            <div style={{
                marginTop: 12,
                marginBottom: 12,
                padding: '12px 14px',
                backgroundColor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 6
            }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>BOM Memo</div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.45, color: '#333' }}>{text}</div>
            </div>
        );
    };

    toggleSubAssemblyExpansion = (subAssemblyId) => {
        const expandedSubAssemblies = new Set(this.state.expandedSubAssemblies);
        if (expandedSubAssemblies.has(subAssemblyId)) {
            expandedSubAssemblies.delete(subAssemblyId);
        } else {
            expandedSubAssemblies.add(subAssemblyId);
        }
        this.setState({ expandedSubAssemblies });
    };

    toggleFamilyExpansion = (familyId) => {
        const expandedFamilies = new Set(this.state.expandedFamilies);
        if (expandedFamilies.has(familyId)) {
            expandedFamilies.delete(familyId);
        } else {
            expandedFamilies.add(familyId);
        }
        this.setState({ expandedFamilies });
    };

    groupPartsByFamily = (partsToAssemblies) => {
        const families = {};
        const individualParts = [];

        (partsToAssemblies || []).forEach((partToAssembly) => {
            if (partToAssembly.partFamilyId) {
                if (!families[partToAssembly.partFamilyId]) {
                    families[partToAssembly.partFamilyId] = {
                        familyId: partToAssembly.partFamilyId,
                        familyName: partToAssembly.partFamilyName,
                        familyDescription: partToAssembly.partFamilyDescription,
                        familyImageURL: partToAssembly.partFamilyImageURL,
                        parts: []
                    };
                }
                families[partToAssembly.partFamilyId].parts.push(partToAssembly);
            } else {
                individualParts.push(partToAssembly);
            }
        });

        // Sort parts within each family by order
        Object.values(families).forEach(family => {
            family.parts.sort((a, b) => (a.order || 0) - (b.order || 0));
        });

        return {
            families: Object.values(families),
            individualParts
        };
    };

    renderSubAssemblyParts = (subAssembly, depth = 1) => {
        // Single ordered list across child subassemblies, parts, and families (matches Edit Assembly)
        const bomLines = groupItemsForBomTable(
            [],
            subAssembly.partsToSubAssemblies,
            subAssembly.childSubAssemblies || []
        );
        const partsHtml = [];

        bomLines.forEach((bomItem) => {
            if (bomItem.itemType === 'subassembly') {
                const childSubAssemblyToSubAssembly = bomItem;
                const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childSubAssemblyToSubAssembly.childSubAssemblyFk);
                if (!childSubAssemblyExpanded || !childSubAssemblyExpanded.subAssembly.isActive) return;
                const childSubAssembly = childSubAssemblyExpanded.subAssembly;
                const isExpanded = this.state.expandedSubAssemblies.has(childSubAssembly.id);

                const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
                const backgroundColor = depth % 2 === 1 ? '#e6f3ff' : '#f0f8ff'; // Alternate colors for depth
                const firstImageUrl = this.getFirstImageUrl(childSubAssembly.imageURL);

                const childSubAssemblyRow = (
                    <tr className={this.getRowStyle(childSubAssemblyToSubAssembly, childSubAssembly.id)} 
                        key={`sub-${subAssembly.id}-child-${childSubAssembly.id}`} 
                        style={{backgroundColor}}>
                        <td style={{textAlign: 'center', padding: '4px'}}>
                            <span 
                                onClick={() => this.toggleSubAssemblyExpansion(childSubAssembly.id)}
                                style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                            >
                                {isExpanded ? '▼' : '▶'}
                            </span>
                        </td>
                        <td className="text-left" style={{paddingLeft: `${paddingLeft}px`, width: 'auto', minWidth: '200px'}}>
                            <div style={{display: 'flex', alignItems: 'baseline'}}>
                                <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{childSubAssembly.subAssemblyId}</span>
                                <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{childSubAssembly.revision}
                            </div>
                            {childSubAssembly.description && (
                                <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                    {childSubAssembly.description}
                                </div>
                            )}
                        </td>
                        <td style={{...getQuantityStyle(childSubAssemblyToSubAssembly.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(childSubAssemblyToSubAssembly.quantity)}</td>
                        <td style={{textAlign: 'center'}}>
                            {firstImageUrl && (
                                <img 
                                    src={firstImageUrl} 
                                    alt="Child SubAssembly"
                                    style={{width: '40px', height: '40px', objectFit: 'cover', cursor: 'pointer'}}
                                    onClick={() => this.openImageModal(firstImageUrl)}
                                />
                            )}
                        </td>
                    </tr>
                );

                partsHtml.push(childSubAssemblyRow);

                // Recursively render the child subassembly's parts and children if expanded
                if (isExpanded) {
                    const childParts = this.renderSubAssemblyParts(childSubAssembly, depth + 1);
                    partsHtml.push(...childParts);
                }
            } else if (bomItem.itemType === 'part') {
            const subAssemblyPart = bomItem;
            const expandedPart = this.state.parts.find(x => x.part.id === subAssemblyPart.partFk);
            if (!expandedPart || expandedPart.part.isActive === false) return;
            const part = expandedPart.part;
            const firstImageUrl = this.getFirstImageUrl(part.imageURL);

            const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
            partsHtml.push(
                <tr className={this.getRowStyle(subAssemblyPart, part.id)} 
                    key={`sub-${subAssembly.id}-part-${part.id}`} 
                    style={{backgroundColor: '#dbdbdb'}}>
                    <td style={{textAlign: 'center', padding: '4px'}}></td>
                    <td className="text-left" style={{paddingLeft: `${paddingLeft}px`, width: 'auto', minWidth: '200px'}}>
                        <PartDisplay part={part} variant="table" classes={this.props.classes} />
                    </td>
                    <td style={{...getQuantityStyle(subAssemblyPart.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyPart.quantity)}</td>
                    <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                        {firstImageUrl ? (
                            <img 
                                src={firstImageUrl} 
                                alt={part.partId}
                                style={{
                                    width: '40px', 
                                    height: '40px', 
                                    objectFit: 'cover', 
                                    cursor: 'pointer',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                                onClick={() => this.openImageModal(firstImageUrl)}
                            />
                        ) : (
                            <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                        )}
                    </td>
                </tr>
            );
            } else if (bomItem.itemType === 'family') {
            const family = bomItem;
            const isExpanded = this.state.expandedFamilies.has(family.familyId);
            const subFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);
            // Family header row
            const familyImageUrl = this.getFirstImageUrl(family.familyImageURL);
            partsHtml.push(
                <tr key={`sub-${subAssembly.id}-family-${family.familyId}`} 
                    className={this.getFamilyRowStyle(family)}
                    style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                    <td style={{textAlign: 'center', padding: '4px'}}>
                        <span 
                            onClick={() => this.toggleFamilyExpansion(family.familyId)}
                            style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                        >
                            {isExpanded ? '▼' : '▶'}
                        </span>
                    </td>
                    <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                        <div style={{display: 'flex', alignItems: 'baseline'}}>
                            <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}><FontAwesomeIcon icon={faPeopleCarry} style={{ marginRight: '6px' }} />{family.familyName}</span>
                        </div>
                        {family.familyDescription && (
                            <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                {family.familyDescription}
                            </div>
                        )}
                    </td>
                    <td style={{...(subFamilyUnitCount != null ? getQuantityStyle(subFamilyUnitCount) : {}), width: '60px', textAlign: 'center'}}>{subFamilyUnitCount != null ? formatQuantity(subFamilyUnitCount) : ''}</td>
                    <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                        {familyImageUrl ? (
                            <img 
                                src={familyImageUrl} 
                                alt={family.familyName}
                                style={{
                                    width: '40px', 
                                    height: '40px', 
                                    objectFit: 'cover', 
                                    cursor: 'pointer',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                                onClick={() => this.openImageModal(familyImageUrl)}
                            />
                        ) : (
                            <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                        )}
                    </td>
                </tr>
            );

            // Family parts - only show if expanded
            if (isExpanded) {
                family.parts.forEach((subAssemblyPart) => {
                    const expandedPart = this.state.parts.find(x => x.part.id === subAssemblyPart.partFk);
                    if (!expandedPart || expandedPart.part.isActive === false) return;
                    const part = expandedPart.part;
                    const firstImageUrl = this.getFirstImageUrl(part.imageURL);

                    const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
                    partsHtml.push(
                        <tr className={this.getRowStyle(subAssemblyPart, part.id)} 
                            key={`sub-${subAssembly.id}-family-${family.familyId}-part-${part.id}`} 
                            style={{backgroundColor: '#f0f8ff'}}>
                            <td style={{textAlign: 'center', padding: '4px'}}></td>
                            <td style={{paddingLeft: `${paddingLeft}px`}} className="text-left">
                                <PartDisplay part={part} variant="table" classes={this.props.classes} />
                            </td>
                            <td style={{...getQuantityStyle(subAssemblyPart.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyPart.quantity)}</td>
                            <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                                {firstImageUrl ? (
                                    <img 
                                        src={firstImageUrl} 
                                        alt={part.partId}
                                        style={{
                                            width: '40px', 
                                            height: '40px', 
                                            objectFit: 'cover', 
                                            cursor: 'pointer',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px'
                                        }}
                                        onClick={() => this.openImageModal(firstImageUrl)}
                                    />
                                ) : (
                                    <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                                )}
                            </td>
                        </tr>
                    );
                });
            }
            }
        });

        return partsHtml;
    };

    render() {
        if (this.state.loading) {
            return (
                this.props.isModal ? (
                    <div className="mobile-container">
                        <CircularProgress/>
                    </div>
                ) : (
                    <WorkshopTemplate>
                        <div className="mobile-container">
                            <CircularProgress/>
                        </div>
                    </WorkshopTemplate>
                )
            );
        }

        let assemblyOptions = [];

        this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((assembly) => {
            assemblyOptions.push(buildAssemblyOption({
                title: `${assembly.subAssembly.subAssemblyId} rev ${assembly.subAssembly.revision}`,
                value: assembly.subAssembly.id,
                description: assembly.subAssembly.description
            }));
        });

        this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
            assemblyOptions.push(buildAssemblyOption({
                title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}`,
                value: assembly.assembly.id,
                description: assembly.assembly.description
            }));
        });

        // Find the selected option for Autocomplete
        const selectedOption = assemblyOptions.find(opt => opt.value === this.state.selectedAssembly) || null;

        // Get assembly info for display
        let assemblyTitle = "";
        if (this.state.selectedAssembly) {
            let assemblyExpandedMaybe = this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly);
            if (assemblyExpandedMaybe && assemblyExpandedMaybe.assembly) {
                assemblyTitle = `${assemblyExpandedMaybe.assembly.assemblyId} rev ${assemblyExpandedMaybe.assembly.revision}`;
            } else {
                let subAssemblyExpandedMaybe = this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly);
                if (subAssemblyExpandedMaybe && subAssemblyExpandedMaybe.subAssembly) {
                    assemblyTitle = `${subAssemblyExpandedMaybe.subAssembly.subAssemblyId} rev ${subAssemblyExpandedMaybe.subAssembly.revision}`;
                }
            }
        }

        let assemblyListHtml = [];
        if (this.state.selectedAssembly) {
            let assemblyExpandedMaybe = this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly);
            let assemblyMaybe = assemblyExpandedMaybe ? this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly).assembly : null;
            let subAssemblyExpandedMaybe = this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly);
            let subAssemblyMaybe = !assemblyMaybe && subAssemblyExpandedMaybe ? this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly).subAssembly : null;

            if (assemblyMaybe) {
                const bomLines = groupItemsForBomTable(
                    assemblyMaybe.subAssembliesToAssemblies,
                    assemblyMaybe.partsToAssemblies
                );
                const rowsHtml = [];

                bomLines.forEach((bomItem) => {
                    if (bomItem.itemType === 'subassembly') {
                    const subAssemblyToAssembly = bomItem;
                    const subAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === subAssemblyToAssembly.subAssemblyFk);
                    if (!subAssemblyExpanded || !subAssemblyExpanded.subAssembly.isActive) return;
                    const subAssembly = subAssemblyExpanded.subAssembly;
                    const isExpanded = this.state.expandedSubAssemblies.has(subAssembly.id);

                    const firstImageUrl = this.getFirstImageUrl(subAssembly.imageURL);
                    
                    const subAssemblyRow = (
                        <tr className={this.getRowStyle(subAssemblyToAssembly, subAssembly.id)} key={`subassembly-${subAssembly.id}`}>
                            <td style={{textAlign: 'center', padding: '4px'}}>
                                <span 
                                    onClick={() => this.toggleSubAssemblyExpansion(subAssembly.id)}
                                    style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                >
                                    {isExpanded ? '▼' : '▶'}
                                </span>
                            </td>
                            <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                <div style={{display: 'flex', alignItems: 'baseline'}}>
                                    <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{subAssembly.subAssemblyId}</span>
                                    <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{subAssembly.revision}
                                </div>
                                {subAssembly.description && (
                                    <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                        {subAssembly.description}
                                    </div>
                                )}
                            </td>
                            <td style={{...getQuantityStyle(subAssemblyToAssembly.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyToAssembly.quantity)}</td>
                            <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                                {firstImageUrl ? (
                                    <img 
                                        src={firstImageUrl} 
                                        alt={subAssembly.subAssemblyId}
                                        style={{
                                            width: '40px', 
                                            height: '40px', 
                                            objectFit: 'cover', 
                                            cursor: 'pointer',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px'
                                        }}
                                        onClick={() => this.openImageModal(firstImageUrl)}
                                    />
                                ) : (
                                    <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                                )}
                            </td>
                        </tr>
                    );

                    const subAssemblyParts = isExpanded ? this.renderSubAssemblyParts(subAssembly) : [];

                    rowsHtml.push(subAssemblyRow, ...subAssemblyParts);
                    } else if (bomItem.itemType === 'part') {
                        const assemblyPart = bomItem;
                        const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                        if (!expandedPart || expandedPart.part.isActive === false) return;
                        const part = expandedPart.part;
                        const firstImageUrl = this.getFirstImageUrl(part.imageURL);

                        rowsHtml.push(
                            <tr className={this.getRowStyle(assemblyPart, part.id)} key={`assembly-part-${part.id}`}>
                                <td style={{textAlign: 'center', padding: '4px'}}></td>
                                <td className="text-left">
                                    <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                </td>
                                <td style={{...getQuantityStyle(assemblyPart.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                                    {firstImageUrl ? (
                                        <img 
                                            src={firstImageUrl} 
                                            alt={part.partId}
                                            style={{
                                                width: '40px', 
                                                height: '40px', 
                                                objectFit: 'cover', 
                                                cursor: 'pointer',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                            onClick={() => this.openImageModal(firstImageUrl)}
                                        />
                                    ) : (
                                        <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                                    )}
                                </td>
                            </tr>
                        );
                    } else if (bomItem.itemType === 'family') {
                        const family = bomItem;
                        const isExpanded = this.state.expandedFamilies.has(family.familyId);
                        const assemblyFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);
                        // Family header row
                        const familyImageUrl = this.getFirstImageUrl(family.familyImageURL);
                        rowsHtml.push(
                            <tr key={`assembly-family-${family.familyId}`} 
                                className={this.getFamilyRowStyle(family)}
                                style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleFamilyExpansion(family.familyId)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {isExpanded ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}><FontAwesomeIcon icon={faPeopleCarry} style={{ marginRight: '6px' }} />{family.familyName}</span>
                                    </div>
                                    {family.familyDescription && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {family.familyDescription}
                                        </div>
                                    )}
                                </td>
                                <td style={{...(assemblyFamilyUnitCount != null ? getQuantityStyle(assemblyFamilyUnitCount) : {}), width: '60px', textAlign: 'center'}}>{assemblyFamilyUnitCount != null ? formatQuantity(assemblyFamilyUnitCount) : ''}</td>
                                <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                                    {familyImageUrl ? (
                                        <img 
                                            src={familyImageUrl} 
                                            alt={family.familyName}
                                            style={{
                                                width: '40px', 
                                                height: '40px', 
                                                objectFit: 'cover', 
                                                cursor: 'pointer',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                            onClick={() => this.openImageModal(familyImageUrl)}
                                        />
                                    ) : (
                                        <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                                    )}
                                </td>
                            </tr>
                        );

                        // Family parts - only show if expanded
                        if (isExpanded) {
                            family.parts.forEach((assemblyPart) => {
                                const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                                if (!expandedPart || expandedPart.part.isActive === false) return;
                                const part = expandedPart.part;
                                const firstImageUrl = this.getFirstImageUrl(part.imageURL);

                                rowsHtml.push(
                                    <tr className={this.getRowStyle(assemblyPart, part.id)} key={`assembly-family-${family.familyId}-part-${part.id}`} 
                                        style={{backgroundColor: '#f0f8ff'}}>
                                        <td style={{textAlign: 'center', padding: '4px'}}></td>
                                        <td style={{paddingLeft: '20px'}} className="text-left" >
                                            <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                        </td>
                                        <td style={{...getQuantityStyle(assemblyPart.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                        <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                                            {firstImageUrl ? (
                                                <img 
                                                    src={firstImageUrl} 
                                                    alt={part.partId}
                                                    style={{
                                                        width: '40px', 
                                                        height: '40px', 
                                                        objectFit: 'cover', 
                                                        cursor: 'pointer',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px'
                                                    }}
                                                    onClick={() => this.openImageModal(firstImageUrl)}
                                                />
                                            ) : (
                                                <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            });
                        }
                    }
                });

                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        <h3 style={{margin: '0'}}>{assemblyMaybe.assemblyId} rev {assemblyMaybe.revision}</h3>
                        {this.renderBomMemo(assemblyMaybe.bomNotes)}
                        <div className="parts-table-container">
                            <table className="parts-table" style={{minWidth: '100%'}}>
                                <thead>
                                <tr>
                                    <th style={{width: '30px'}}></th>
                                    <th style={{width: 'auto', minWidth: '200px'}}>Part Number + Description</th>
                                    <th style={{width: '60px'}}>Qty</th>
                                    <th style={{width: '60px'}}>Image</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rowsHtml}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            } else {
                // Single ordered list across child subassemblies, parts, and families (matches Edit Assembly)
                const bomLines = groupItemsForBomTable(
                    [],
                    subAssemblyMaybe.partsToSubAssemblies,
                    subAssemblyMaybe.childSubAssemblies || []
                );
                const rowsHtml = [];

                bomLines.forEach((bomItem) => {
                    if (bomItem.itemType === 'subassembly') {
                        const childSubAssemblyToSubAssembly = bomItem;
                        const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childSubAssemblyToSubAssembly.childSubAssemblyFk);
                        if (!childSubAssemblyExpanded || !childSubAssemblyExpanded.subAssembly.isActive) return;
                        const childSubAssembly = childSubAssemblyExpanded.subAssembly;
                        const isExpanded = this.state.expandedSubAssemblies.has(childSubAssembly.id);
                        
                        const firstImageUrl = this.getFirstImageUrl(childSubAssembly.imageURL);

                        const childSubAssemblyRow = (
                            <tr className={this.getRowStyle(childSubAssemblyToSubAssembly, childSubAssembly.id)} key={`child-subassembly-${childSubAssembly.id}`}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleSubAssemblyExpansion(childSubAssembly.id)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {isExpanded ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{childSubAssembly.subAssemblyId}</span>
                                        <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{childSubAssembly.revision}
                                    </div>
                                    {childSubAssembly.description && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {childSubAssembly.description}
                                        </div>
                                    )}
                                </td>
                                <td style={{...getQuantityStyle(childSubAssemblyToSubAssembly.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(childSubAssemblyToSubAssembly.quantity)}</td>
                                <td style={{textAlign: 'center'}}>
                                    {firstImageUrl && (
                                        <img 
                                            src={firstImageUrl} 
                                            alt="Child SubAssembly"
                                            style={{width: '40px', height: '40px', objectFit: 'cover', cursor: 'pointer'}}
                                            onClick={() => this.openImageModal(firstImageUrl)}
                                        />
                                    )}
                                </td>
                            </tr>
                        );

                        const childSubAssemblyParts = isExpanded ? this.renderSubAssemblyParts(childSubAssembly) : [];

                        rowsHtml.push(childSubAssemblyRow, ...childSubAssemblyParts);
                    } else if (bomItem.itemType === 'part') {
                        const assemblyPart = bomItem;
                        const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                        if (!expandedPart || expandedPart.part.isActive === false) return;
                        const part = expandedPart.part;
                        const firstImageUrl = this.getFirstImageUrl(part.imageURL);

                        rowsHtml.push(
                            <tr className={this.getRowStyle(assemblyPart, part.id)} key={`subassembly-part-${part.id}`}>
                                <td style={{textAlign: 'center', padding: '4px'}}></td>
                                <td className="text-left">
                                    <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                </td>
                                <td style={{...getQuantityStyle(assemblyPart.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                                    {firstImageUrl ? (
                                        <img 
                                            src={firstImageUrl} 
                                            alt={part.partId}
                                            style={{
                                                width: '40px', 
                                                height: '40px', 
                                                objectFit: 'cover', 
                                                cursor: 'pointer',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                            onClick={() => this.openImageModal(firstImageUrl)}
                                        />
                                    ) : (
                                        <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                                    )}
                                </td>
                            </tr>
                        );
                    } else if (bomItem.itemType === 'family') {
                        const family = bomItem;
                        const isExpanded = this.state.expandedFamilies.has(family.familyId);
                        const nestedSubFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);
                        // Family header row
                        const familyImageUrl = this.getFirstImageUrl(family.familyImageURL);
                        rowsHtml.push(
                            <tr key={`subassembly-family-${family.familyId}`} 
                                className={this.getFamilyRowStyle(family)}
                                style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleFamilyExpansion(family.familyId)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {isExpanded ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}><FontAwesomeIcon icon={faPeopleCarry} style={{ marginRight: '6px' }} />{family.familyName}</span>
                                    </div>
                                    {family.familyDescription && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {family.familyDescription}
                                        </div>
                                    )}
                                </td>
                                <td style={{...(nestedSubFamilyUnitCount != null ? getQuantityStyle(nestedSubFamilyUnitCount) : {}), width: '60px', textAlign: 'center'}}>{nestedSubFamilyUnitCount != null ? formatQuantity(nestedSubFamilyUnitCount) : ''}</td>
                                <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                                    {familyImageUrl ? (
                                        <img 
                                            src={familyImageUrl} 
                                            alt={family.familyName}
                                            style={{
                                                width: '40px', 
                                                height: '40px', 
                                                objectFit: 'cover', 
                                                cursor: 'pointer',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                            onClick={() => this.openImageModal(familyImageUrl)}
                                        />
                                    ) : (
                                        <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                                    )}
                                </td>
                            </tr>
                        );

                        // Family parts - only show if expanded
                        if (isExpanded) {
                            family.parts.forEach((assemblyPart) => {
                                const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                                if (!expandedPart || expandedPart.part.isActive === false) return;
                                const part = expandedPart.part;
                                const firstImageUrl = this.getFirstImageUrl(part.imageURL);

                                rowsHtml.push(
                                    <tr className={this.getRowStyle(assemblyPart, part.id)} key={`subassembly-family-${family.familyId}-part-${part.id}`} 
                                        style={{backgroundColor: '#f0f8ff'}}>
                                        <td style={{textAlign: 'center', padding: '4px'}}></td>
                                        <td style={{paddingLeft: '20px'}} className="text-left">
                                            <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                        </td>
                                        <td style={{...getQuantityStyle(assemblyPart.quantity), width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                        <td style={{width: '60px', height: '46px', textAlign: 'center'}}>
                                            {firstImageUrl ? (
                                                <img 
                                                    src={firstImageUrl} 
                                                    alt={part.partId}
                                                    style={{
                                                        width: '40px', 
                                                        height: '40px', 
                                                        objectFit: 'cover', 
                                                        cursor: 'pointer',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px'
                                                    }}
                                                    onClick={() => this.openImageModal(firstImageUrl)}
                                                />
                                            ) : (
                                                <span style={{color: '#999', fontSize: '12px'}}>No Image</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            });
                        }
                    }
                });

                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        <h3>{subAssemblyMaybe.subAssemblyId} rev {subAssemblyMaybe.revision}</h3>
                        {this.renderBomMemo(subAssemblyMaybe.bomNotes)}
                        <div className="parts-table-container">
                            <table className="parts-table" style={{minWidth: '100%'}}>
                                <thead>
                                <tr>
                                    <th style={{width: '30px'}}></th>
                                    <th style={{width: 'auto', minWidth: '200px'}}>Part Number + Description</th>
                                    <th style={{width: '60px'}}>Qty</th>
                                    <th style={{width: '60px'}}>Image</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rowsHtml}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            }
        }

        const assemblyId = globalProps.match?.params?.assemblyId;

        const content = (
            <div>
                {!this.props.isModal && (
                    <Autocomplete autoHighlight
                        id="ddlAssembly"
                        options={assemblyOptions}
                        onChange={this.handleAssemblyChange}
                        value={selectedOption}
                        getOptionLabel={(option) => option.title}
                        renderOption={(option) => (
                            <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                                <span style={{fontWeight: 600, color: '#222'}}>{option.title}</span>
                                {option.description ? <span style={{fontSize: '12px', color: '#666'}}>{option.description}</span> : null}
                            </div>
                        )}
                        className="autocomplete-dropdown"
                        style={{maxWidth: 600, marginBottom: 20}}
                        renderInput={(params) => <TextField {...params} inputProps={{
                            ...params.inputProps
                        }} label="Assembly" variant="outlined" autoComplete="new-password"/>}
                    />
                )}
                <div style={{ margin: '0 auto' }}>
                    <Button 
                        onClick={() => this.setState({ showScanner: !this.state.showScanner })}
                        className="success-button-lg" style={{marginRight: 10}}
                        disabled={!this.state.selectedAssembly}
                    >{this.state.showScanner ? "Hide Scanner" : "Start BOM Check"} </Button>
                    {this.state.scannedItems.size > 0 && (
                        <Button 
                            onClick={() => this.setState({ scannedItems: new Set() })}
                            className="success-button-lg"
                        >Reset BOM</Button>
                    )}
                </div>
                {this.state.showScanner && (
                    <div style={{ margin: '5px 0', padding: '5px', border: '1px solid #ccc', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
                        <h3 style={{ margin: '0 0 5px 0' }}>Scan QR Code</h3>
                        <QrScanner addQrScan={this.addQrScan} />
                    </div>
                )}
                
                {assemblyListHtml}
                
                <div style={{ margin: '10px 0', textAlign: 'center' }}>
                    {this.areAllRequiredItemsScanned() && !this.state.bomCheckCompleted ? (
                        <Button 
                            onClick={this.handleCompleteBomCheck}
                            className="success-button-lg"
                            style={{ width: '200px' }}
                        >Complete BOM Check</Button>
                    ) : ""}
                    {this.state.bomCheckCompleted && 
                        <div style={{ marginTop: '10px', color: 'green', fontWeight: 'bold' }}>
                            BOM Check completed!
                        </div>
                    }
                </div>
            </div>
        );

        // Image modal for viewing larger images
        const imageModal = (
            <Dialog
                open={this.state.imageModalOpen}
                onClose={this.closeImageModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent style={{padding: '20px', textAlign: 'center'}}>
                    {this.state.selectedImageUrl && (
                        <img 
                            src={this.state.selectedImageUrl} 
                            alt="Part/Assembly Image"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain'
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.closeImageModal}>Close</Button>
                </DialogActions>
            </Dialog>
        );

        // Return content wrapped in dialog when used as modal
        if (this.props.isModal) {
            return (
                <>
                    <Dialog 
                        open={this.props.open} 
                        onClose={this.props.onClose}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogContent>
                            {content}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={this.props.onClose}>Close</Button>
                        </DialogActions>
                    </Dialog>
                    {imageModal}
                </>
            );
        }

        return (
            <WorkshopTemplate>
                {content}
                {imageModal}
            </WorkshopTemplate>
        );
    }
}

let globalProps;

BomCheck.propTypes = {
    classes: PropTypes.object.isRequired,
    isModal: PropTypes.bool,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    assemblyId: PropTypes.string,
    onComplete: PropTypes.func
};

export default withStyles(styles)(BomCheck);
